import Vue from 'vue'
import VueRouter from 'vue-router'
import i18n from '@/locales'
import { ensureCameraPermission } from '@/utils/cameraPermissionGuard'
import { extractDynaLiveWebCallbackFromUrl, isValidDynaLiveWebCallback } from '@/utils/dynaLiveWeb'

Vue.use(VueRouter)

function buildRouteGuardI18nVm() {
  return {
    $t(key) {
      return i18n.t(key)
    }
  }
}

async function ensureCameraPermissionBeforeFaceRoute(to, from, next) {
  const dynaLiveWebCallback = extractDynaLiveWebCallbackFromUrl(to.fullPath || '')
  if (isValidDynaLiveWebCallback(dynaLiveWebCallback)) {
    next()
    return
  }

  const hasPermission = await ensureCameraPermission(buildRouteGuardI18nVm())
  next(hasPermission ? undefined : false)
}

const routes = [
  {
    path: '/',
    redirect: '/mainPage/home'
  },
  {
    path: '/login',
    name: 'Login',
    meta: { title: 'login' },
    component: () => import('@/views/bluepay/login.vue')
  },
  // {
  //   path: '/register',
  //   name: 'Register',
  //   meta: { title: 'register' },
  //   component: () => import('@/views/register.vue')
  // },
  {
    path: '/notify',
    name: 'notify',
    meta: { title: 'reset_password' },
    component: () => import('@/views/bluepay/notify/notify.vue')
  },
  {
    path: '/resetPassword',
    name: 'ResetPassword',
    meta: { title: 'reset_password' },
    component: () => import('@/views/bluepay/reset-password.vue')
  },
  {
    path: '/checkPin',
    name: 'CheckPin',
    meta: { title: 'pin' },
    component: () => import('@/views/bluepay/pin/check-pin.vue')
  },
  {
    path: '/pinWithOtp',
    name: 'PinWithOtp',
    meta: { title: 'pin' },
    component: () => import('@/views/bluepay/pin/pin-with-otp.vue')
  },
  {
    path: '/pinWithFace',
    name: 'PinWithFace',
    meta: { title: 'pin' },
    beforeEnter: ensureCameraPermissionBeforeFaceRoute,
    component: () => import('@/views/bluepay/pin/pin-with-face.vue')
  },
  {
    path: '/unlockPinWithFace',
    name: 'UnlockPinWithFace',
    meta: { title: 'pin' },
    beforeEnter: ensureCameraPermissionBeforeFaceRoute,
    component: () => import('@/views/bluepay/pin/unlock-pin-with-face.vue')
  },
  {
    path: '/pinToAddFace',
    name: 'PinToAddFace',
    meta: { title: 'pin' },
    component: () => import('@/views/bluepay/pin/pin-to-add-face.vue')
  },
  {
    path: '/pinWithOldPin',
    name: 'PinWithOldPin',
    meta: { title: 'pin' },
    component: () => import('@/views/bluepay/pin/pin-with-old-pin.vue')
  },
  {
    path: '/pinWithFaceFail',
    name: 'PinWithFaceFail',
    meta: { title: 'pin' },
    component: () => import('@/views/bluepay/pin/pin-with-face-fail.vue')
  },
  {
    path: '/pinSetUp',
    name: 'PinSetUp',
    meta: { title: 'pin' },
    component: () => import('@/views/bluepay/pin/pin-set-up.vue')
  },
  {
    path: '/pinComplete',
    name: 'PinComplete',
    meta: { title: 'pin' },
    component: () => import('@/views/bluepay/pin/pin-complete.vue')
  },

  {
    path: '/accountLimits',
    name: 'AccountLimits',
    meta: { title: 'account_limits' },
    component: () => import('@/views/bluepay/settings/account-limits.vue')
  },

  {
    path: '/verificationLevels',
    name: 'VerificationLevels',
    meta: { title: 'verification_levels' },
    component: () => import('@/views/bluepay/settings/verification-levels.vue')
  },
  {
    path: '/withholding',
    name: 'Withholding',
    meta: { title: 'withholding' },
    component: () => import('@/views/bluepay/withholding/activate-auto-debit.vue')
  },
  {
    path: '/protocol',
    name: 'Protocol',
    meta: { title: 'protocol' },
    component: () => import('@/views/bluepay/withholding/protocol/protocol.vue')
  },
  {
    path: '/protocolTh',
    name: 'ProtocolTh',
    meta: { title: 'protocol' },
    component: () => import('@/views/bluepay/withholding/protocol/protocol-th.vue')
  },
  {
    path: '/newProtocol',
    name: 'newProtocol',
    meta: { title: 'protocol' },
    component: () => import('@/views/bluepay/withholding/protocol/new-protocol.vue')
  },
  {
    path: '/activationSuccessful',
    name: 'ActivationSuccessful',
    meta: { title: 'activationSuccessful' },
    component: () => import('@/views/bluepay/withholding/activation-successful.vue')
  },
  {
    path: '/autoDebit',
    name: 'AutoDebit',
    meta: { title: 'autoDebit' },
    component: () => import('@/views/bluepay/withholding/auto-debit.vue')
  },
  {
    path: '/merchantInformation',
    name: 'merchantInformation',
    meta: { title: 'merchantInformation' },
    component: () => import('@/views/bluepay/withholding/merchant-information.vue')
  },
  {
    path: '/terminationCompleted',
    name: 'terminationCompleted',
    meta: { title: 'terminationCompleted' },
    component: () => import('@/views/bluepay/withholding/termination-completed.vue')
  },
  {
    path: '/helpCenter',
    name: 'HelpCenter',
    meta: { title: 'help_center' },
    component: () => import('@/views/bluepay/settings/help-center.vue')
  },
  {
    path: '/privacy',
    name: 'Privacy',
    meta: { title: 'privacy' },
    component: () => import('@/views/bluepay/settings/privacy.vue')
  },
  {
    path: '/language',
    name: 'Language',
    meta: { title: 'language' },
    component: () => import('@/views/bluepay/settings/language.vue')
  },
  {
    path: '/kycPrivacy',
    name: 'kycPrivacy',
    meta: { title: 'privacy' },
    component: () => import('@/common/kyc-privacy.vue')
  },
  {
    path: '/faq',
    name: 'Faq',
    meta: { title: 'faq' },
    component: () => import('@/views/bluepay/settings/faq.vue')
  },
  {
    path: '/feedback',
    name: 'Feedback',
    meta: { title: 'feedback' },
    component: () => import('@/views/bluepay/settings/feedback.vue')
  },
  {
    path: '/feedBackHistory',
    name: 'FeedBackHistory',
    meta: { title: 'feedback_history' },
    component: () => import('@/views/bluepay/settings/feedback-history.vue')
  },
  {
    path: '/feedBackResult',
    name: 'FeedBackResult',
    meta: { title: 'feedback_history' },
    component: () => import('@/views/bluepay/settings/feedback-result.vue')
  },
  {
    path: '/404',
    meta: { title: 'page_not_found' },
    component: () => import('@/views/bluepay/404.vue')
  },
  {
    name: 'TransactionDetail',
    path: '/transactionDetail',
    meta: { title: 'historyDetail' },
    component: () => import('@/views/bluepay/order/history-detail.vue'),
    hidden: true
  },
  {
    name: 'QRTransactionDetail',
    path: '/qrTransactionDetail',
    meta: { title: 'historyDetail' },
    component: () => import('@/views/bluepay/order/history-QR-detail.vue'),
    hidden: true
  },
  {
    path: '/SplashPage',
    component: () => import('@/views/bluepay/splash-page.vue')
  },
  {
    name: 'TopUpStatus',
    path: '/topUp/status/:id',
    meta: { title: 'top_up' },
    component: () => import('@/views/bluepay/account/top-up/check-top-up-status.vue')
  },
  {
    name: 'CashOutStatus',
    path: '/cashOut/status/:id',
    meta: { title: 'cash_out' },
    component: () => import('@/views/bluepay/account/cash-out/check-cash-out-status.vue')
  },
  {
    name: 'TransferOutStatus',
    path: '/transfer/status/:id',
    meta: { title: 'transfer' },
    component: () => import('@/views/bluepay/account/transfer/check-transfer-out-status.vue')
  },
  {
    name: 'TopUpMobileStatus',
    path: '/topUpMobile/status/:id',
    meta: { title: 'mobile_top_up' },
    component: () => import('@/views/bluepay/account/top-up/check-top-up-mobile-status.vue')
  },
  {
    name: 'BankAccountsList',
    path: '/bankAccounts',
    component: () => import('@/views/bluepay/account/bank-accounts-list.vue'),
    meta: {
      title: 'Bank Accounts'
    }
  },
  {
    name: 'PromotionList',
    path: '/promotion',
    component: () => import('@/views/bluepay/account/promotion-list.vue'),
    meta: {
      title: 'PromotionList'
    }
  },
  {
    name: 'AddBankAccount',
    path: '/addBankAccount',
    component: () => import('@/views/bluepay/account/add-bank-account.vue'),
    meta: {
      title: 'new_account'
    }
  },
  {
    name: 'IdentityChoose',
    path: '/identityChoose',
    component: () => import('@/views/bluepay/account/identity/identity-choose.vue'),
    meta: {
      title: 'identity'
    }
  },
  {
    name: 'IdentityEnterprise',
    path: '/identityEnterprise',
    component: () => import('@/views/bluepay/account/identity/identity-enterprise.vue'),
    meta: {
      title: 'identity'
    }
  },
  {
    name: 'Identity',
    path: '/identity',
    component: () => import('@/views/bluepay/account/identity/identity-information.vue'),
    meta: {
      title: 'identity'
    }
  },
  {
    name: 'IdentityView',
    path: '/identity-view',
    component: () => import('@/views/bluepay/account/identity/identity-information-view.vue'),
    meta: {
      title: 'identity-information-view'
    }
  },
  {
    name: 'DopaAuthentication',
    path: '/dopaAuthentication',
    component: () => import('@/views/bluepay/account/dopa-enhanced-kyc/dopa-authentication.vue'),
    meta: {
      title: 'dopaAuthentication'
    }
  },
  {
    name: 'DopaAuthenticationManual',
    path: '/dopaAuthenticationManual',
    component: () => import('@/views/bluepay/account/dopa-enhanced-kyc/dopa-authentication-manual.vue'),
    meta: {
      title: 'dopaAuthenticationManual'
    }
  },
  {
    name: 'DopaAuthenticationStatus',
    path: '/dopaAuthenticationStatus',
    component: () => import('@/views/bluepay/account/dopa-enhanced-kyc/dopa-authentication-status.vue'),
    meta: {
      title: 'dopaAuthenticationStatus'
    }
  },
  {
    name: 'RegisterAuth',
    path: '/registerAuth',
    component: () => import('@/views/bluepay/account/zoloz-enhanced-kyc/register-auth.vue'),
    meta: {
      title: 'RegisterAuth'
    }
  },
  {
    name: 'RegisterInfoConfirm',
    path: '/registerInfoConfirm',
    component: () => import('@/views/bluepay/account/zoloz-enhanced-kyc/register-info-confirm.vue'),
    meta: {
      title: 'RegisterInfoConfirm'
    }
  },
  {
    name: 'RegisterInfoDetail',
    path: '/registerInfoDetail',
    component: () => import('@/views/bluepay/account/zoloz-enhanced-kyc/register-info-detail.vue'),
    meta: {
      title: 'RegisterInfoDetail'
    }
  },
  {
    name: 'ServiceCertification',
    path: '/serviceCertification',
    component: () => import('@/common/service-certification.vue'),
    meta: {
      title: 'service_certification'
    }
  },
  {
    name: 'CertificationForZolozEnhance',
    path: '/certificationForZolozEnhance',
    component: () => import('@/common/certification-for-zoloz-enhance.vue'),
    meta: {
      title: 'service_certification'
    }
  },
  {
    name: 'ServicePolicy',
    path: '/servicePolicy',
    component: () => import('@/views/bluepay/settings/service-policy.vue'),
    meta: {
      title: 'service_agreement_certification'
    }
  },
  {
    name: 'PrivacyAgreement',
    path: '/privacyAgreement',
    component: () => import('@/views/bluepay/settings/privacy-agreement.vue'),
    meta: {
      title: 'privacy_agreement'
    }
  },
  {
    name: 'MainPage',
    path: '/mainPage',
    redirect: '/mainPage/home',
    component: () => import('@/views/bluepay/main-page.vue'),
    meta: { keepAlive: true },
    hidden: true,
    children: [
      {
        name: 'home',
        path: 'home',
        component: () => import('@/views/bluepay/tabbar/home.vue'),
        meta: {
          keepAlive: true,
          keepScroll: true,
          scrollTop: 0,
          scrollContainer: '.main-router-view',
          title: 'home'
        }
      },
      {
        name: 'transactions',
        path: 'transactions',
        component: () => import('@/views/bluepay/tabbar/transactions.vue'),
        meta: {
          keepAlive: true,
          keepScroll: true,
          scrollTop: 0,
          scrollContainer: '.main-router-view',
          title: 'transactions'
        }
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('@/views/bluepay/tabbar/settings.vue'),
        meta: {
          keepAlive: true,
          title: 'profile'
        }
      }
    ]
  },
  {
    name: 'Assets',
    path: '/assets',
    component: () => import('@/views/bluepay/account/assets.vue'),
    meta: {
      title: 'assets'
    }
  },
  {
    name: 'Transfer',
    path: '/transfer',
    meta: { title: 'Transfer' },
    component: () => import('@/views/bluepay/account/transfer/transfer.vue')
  },
  {
    name: 'CashOut',
    path: '/cashOut',
    meta: { title: 'cash_out' },
    component: () => import('@/views/bluepay/account/cash-out/cash-out.vue')
  },
  {
    name: 'TopUp',
    path: '/topUp',
    meta: { title: 'top_up' },
    component: () => import('@/views/bluepay/account/top-up/top-up.vue')
  },
  {
    name: 'MobileTopUp',
    path: '/topUp/mobile',
    meta: { title: 'mobile_top_up' },
    component: () => import('@/views/bluepay/account/top-up/top-up-mobile.vue')
  },
  {
    name: 'DeactivateCanNot',
    path: '/deactivateCanNot',
    meta: { title: 'deactivate_account' },
    component: () => import('@/views/bluepay/deactivate/deactivate-can-not.vue')
  },
  {
    name: 'DeactivateSuccess',
    path: '/deactivateSuccess',
    meta: { title: 'deactivate_account' },
    component: () => import('@/views/bluepay/deactivate/deactivate-success.vue')
  },
  {
    name: 'DeactivateVerify',
    path: '/deactivateVerify',
    meta: { title: 'deactivate_account' },
    component: () => import('@/views/bluepay/deactivate/deactivate-verify.vue')
  }
]

const asyncRoutes = []

function integrationRoutes() {
  const integrationRoutes = []

  asyncRoutes.forEach(route => {
    route.forEach(item => {
      integrationRoutes.push(item)
    })
  })
  integrationRoutes.push({ path: '*', redirect: '/bluepay/404', hidden: true })
  return routes.concat(integrationRoutes)
}

const router = new VueRouter({
  mode: 'hash',
  routes: integrationRoutes()
})
router.beforeEach((to, from, next) => {
  //console.log('🌐 全局监听：即将从', from.fullPath, '到', to.fullPath)
  // 定义页面白名单
  const personSavePage = new Set(['/kycPrivacy?nationType=blue'])
  const enterpriseSavePage = new Set(['/registerInfoConfirm'])

  // 配置来源路由与对应的清理规则
  const clearRules = {
    '/identity': {
      exclude: personSavePage,
      keys: ['checkBoxStatus', 'nationalType']
    },
    '/registerAuth': {
      exclude: new Set([...personSavePage, ...enterpriseSavePage]),
      keys: ['zolozRegisterIsChecked']
    }
  }

  // 清理逻辑
  function clearSessionStorage(from, to) {
    const rule = clearRules[from.fullPath]
    if (!rule) return

    // 如果目标路由不在排除列表中，清理对应 sessionStorage
    if (!rule.exclude.has(to.fullPath)) {
      rule.keys.forEach(key => sessionStorage.removeItem(key))
    }
  }

  // 调用
  clearSessionStorage(from, to)
  next()
})

router.afterEach(() => {
  // console.log('🌐 路由已跳转到', to.fullPath)
})
export default router
